/**
 * VoitureArticle — Affichage d'une enquête magazine ou d'un satellite voiture.
 * Routes : /voiture/magazine/{slug}  ou  /voiture/apprendre/{parent}/{slug}
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock, AlertTriangle, Lightbulb, Info, XCircle, Quote as QuoteIcon } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function Block({ b }) {
  if (!b || !b.type) return null;
  if (b.type === 'paragraph') return <p className="text-base sm:text-lg leading-relaxed text-foreground/85 my-4" dangerouslySetInnerHTML={{ __html: b.text }} />;
  if (b.type === 'subtitle') return <h2 className="text-xl sm:text-2xl font-black tracking-tight mt-8 mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{b.text}</h2>;
  if (b.type === 'keypoints') return (
    <div className="my-5 rounded-xl bg-muted/40 border border-border p-4 sm:p-5">
      {b.title && <div className="text-[11px] font-black uppercase tracking-[0.18em] mb-2" style={{ color: '#1d4ed8' }}>{b.title}</div>}
      <ul className="space-y-2">
        {(b.items || []).map((it, i) => (
          <li key={i} className="flex gap-2 text-sm sm:text-base leading-relaxed">
            <span className="text-[#1d4ed8] flex-shrink-0 font-bold">•</span>
            <span dangerouslySetInnerHTML={{ __html: it }} />
          </li>
        ))}
      </ul>
    </div>
  );
  if (b.type === 'callout') {
    const tone = b.tone || 'info';
    const conf = {
      warning: { icon: AlertTriangle, color: '#ea580c', bg: 'rgba(234, 88, 12, 0.08)' },
      tip: { icon: Lightbulb, color: '#16a34a', bg: 'rgba(22, 163, 74, 0.08)' },
      info: { icon: Info, color: '#1d4ed8', bg: 'rgba(29, 78, 216, 0.08)' },
      mistake: { icon: XCircle, color: '#dc2626', bg: 'rgba(220, 38, 38, 0.08)' },
    }[tone] || { icon: Info, color: '#1d4ed8', bg: 'rgba(29, 78, 216, 0.08)' };
    const Icon = conf.icon;
    return (
      <div className="my-5 rounded-xl border-l-4 p-4 sm:p-5" style={{ borderColor: conf.color, background: conf.bg }}>
        <div className="flex items-start gap-2.5">
          <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: conf.color }} />
          <div className="flex-1">
            {b.title && <div className="font-black text-sm mb-1">{b.title}</div>}
            <div className="text-sm leading-relaxed text-foreground/85" dangerouslySetInnerHTML={{ __html: b.text }} />
          </div>
        </div>
      </div>
    );
  }
  if (b.type === 'quote') return (
    <blockquote className="my-6 pl-4 border-l-4 italic" style={{ borderColor: '#1d4ed8' }}>
      <QuoteIcon className="w-5 h-5 mb-1" style={{ color: '#1d4ed8' }} />
      <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">{b.text}</p>
      {b.source && <footer className="text-xs text-foreground/60 mt-2 not-italic font-bold">— {b.source}</footer>}
    </blockquote>
  );
  if (b.type === 'sources') return (
    <div className="mt-8 pt-4 border-t border-border">
      <div className="text-[11px] font-black uppercase tracking-[0.18em] mb-2 text-foreground/60">Sources</div>
      <ul className="text-xs text-foreground/60 space-y-1">
        {(b.items || []).map((s, i) => (
          <li key={i}>{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{s.label}</a> : s.label}</li>
        ))}
      </ul>
    </div>
  );
  return null;
}

export default function VoitureArticle() {
  const { slug, parentSlug } = useParams();
  const location = useLocation();
  const isSatellite = location.pathname.includes('/apprendre/');
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    const url = isSatellite
      ? `${API}/api/voiture/apprendre/${parentSlug}/${slug}`
      : `${API}/api/voiture/magazine/${slug}`;
    axios.get(url)
      .then((r) => { setArticle(r.data); document.title = `${r.data.title} · Code 2 Roues`; })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, parentSlug, isSatellite]);

  if (loading) return <div className="max-w-3xl mx-auto p-8 text-foreground/50">Chargement…</div>;
  if (notFound || !article) return (
    <div className="max-w-3xl mx-auto p-8">
      <p className="text-foreground/60">Article introuvable.</p>
      <Link to="/voiture" className="inline-flex items-center gap-1 mt-4 text-sm font-bold" style={{ color: '#1d4ed8' }}>
        <ArrowLeft className="w-4 h-4" /> Espace Voiture
      </Link>
    </div>
  );

  const backTo = isSatellite ? `/voiture/apprendre` : '/voiture/magazine';
  const backLabel = isSatellite ? 'Apprendre' : 'Magazine';

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="voiture-article">
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> {backLabel} Voiture
      </Link>
      {article.kicker && (
        <div className="text-[11px] font-black uppercase tracking-[0.22em] mb-2" style={{ color: '#1d4ed8' }}>
          {article.kicker}
        </div>
      )}
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-foreground leading-[1.1] mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {article.title}
      </h1>
      {article.teaser && <p className="text-lg sm:text-xl text-foreground/75 leading-relaxed mb-4">{article.teaser}</p>}
      <div className="flex items-center gap-3 text-xs text-foreground/50 pb-6 border-b border-border">
        {article.readTimeMin && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.readTimeMin} min de lecture</span>}
        {article.publishedAt && <span>· Publié le {new Date(article.publishedAt).toLocaleDateString('fr-FR')}</span>}
      </div>
      <div className="mt-6">
        {(article.body || []).map((b, i) => <Block key={i} b={b} />)}
      </div>
    </article>
  );
}
